/**
 * Reviews Videos — Horizon
 *
 * - Autoplay when in viewport (muted by default, fallback if browser blocks)
 * - One unmuted grid video at a time
 * - Marquee mode (> 4 videos): continuous rAF strip, pauses on hover/touch, draggable
 * - Lightbox: fullscreen player whose mute state is
 *   fully independent from the grid
 */

/* ============================================================
   ReviewsVideos custom element
   ============================================================ */
class ReviewsVideos extends HTMLElement {
  connectedCallback() {
    this.mode          = this.dataset.mode        || 'slider';
    this.autoplay      = this.dataset.autoplay    === 'true';
    this.mutedDefault  = this.dataset.muted       !== 'false';
    this.lightboxMuted = this.dataset.lightboxMuted === 'true';

    this.labels = {
      mute:   this.dataset.labelMute   || 'Mute video',
      unmute: this.dataset.labelUnmute || 'Unmute video',
    };

    this.track = this.querySelector('[data-rv-track]');

    if (this.mode === 'marquee') this._setupMarquee();

    this.videos = [...this.querySelectorAll('video.rv-video')];
    this._initVideos();

    if (this.autoplay && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) =>
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) this._tryPlay(target);
            else target.pause();
          }),
        { threshold: 0.35 }
      );
      this.videos.forEach((v) => this.observer.observe(v));
    }

    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this._onClick);
    this._teardownMarquee();
  }

  /* ---- Video helpers ---- */

  _initVideos() {
    this.videos.forEach((v) => {
      v.muted        = this.mutedDefault;
      v.defaultMuted = this.mutedDefault;
      const item = v.closest('.rv-item');
      item?.classList.toggle('is-unmuted', !this.mutedDefault);
      item?.querySelector('[data-rv-mute]')?.setAttribute(
        'aria-label',
        this.mutedDefault ? this.labels.unmute : this.labels.mute
      );
    });
  }

  _tryPlay(video) {
    video.play().catch(() => {
      if (!video.muted) {
        // Browser blocked unmuted autoplay — fall back to muted
        video.muted = true;
        const item = video.closest('.rv-item');
        item?.classList.remove('is-unmuted');
        item?.querySelector('[data-rv-mute]')?.setAttribute('aria-label', this.labels.unmute);
        video.play().catch(() => {});
      }
    });
  }

  _onClick = (event) => {
    // Mute / unmute button inside the grid
    const muteBtn = event.target.closest('[data-rv-mute]');
    if (muteBtn) {
      event.preventDefault();
      event.stopPropagation();
      this._toggleMute(muteBtn.closest('.rv-item'));
      return;
    }

    // Click on the video area → open fullscreen lightbox
    const media = event.target.closest('[data-rv-open-lightbox]');
    if (media) {
      event.preventDefault();
      const video = media.querySelector('video');
      if (!video) return;
      this._pauseAll();
      // Lightbox mute is independent — driven by the section setting, not the grid button
      document.querySelector('review-video-lightbox')?.open(video, { muted: this.lightboxMuted });
    }
  };

  _toggleMute(item) {
    const video = item?.querySelector('video');
    if (!video) return;

    const willUnmute = video.muted;

    if (willUnmute) {
      // Ensure only one grid video plays with sound at a time
      this.videos.forEach((v) => {
        v.muted = true;
        const i = v.closest('.rv-item');
        i?.classList.remove('is-unmuted');
        i?.querySelector('[data-rv-mute]')?.setAttribute('aria-label', this.labels.unmute);
      });
    }

    video.muted = !willUnmute;
    item.classList.toggle('is-unmuted', willUnmute);
    item
      .querySelector('[data-rv-mute]')
      ?.setAttribute('aria-label', willUnmute ? this.labels.mute : this.labels.unmute);

    if (willUnmute && video.paused) this._tryPlay(video);
  }

  _pauseAll() {
    this.pausedByModal = true;
    // Pause playback only; preserve each video's muted state so it
    // resumes correctly when the lightbox closes.
    this.videos.forEach((v) => v.pause());
  }

  resumeVisible() {
    this.pausedByModal = false;
    if (!this.autoplay) return;
    this.videos.forEach((v) => {
      const r = v.getBoundingClientRect();
      const visible =
        r.top < window.innerHeight &&
        r.bottom > 0 &&
        r.left < window.innerWidth &&
        r.right > 0;
      if (visible) this._tryPlay(v);
    });
  }

  /* ---- Marquee ---- */

  _setupMarquee() {
    if (!this.track) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-static');
      return;
    }

    // Clone items once for a seamless infinite loop
    if (!this.track.dataset.cloned) {
      [...this.track.children].forEach((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('rv-item--clone');
        clone.setAttribute('aria-hidden', 'true');
        [...clone.attributes].forEach((a) => {
          if (a.name.startsWith('data-shopify')) clone.removeAttribute(a.name);
        });
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      });
      this.track.dataset.cloned = 'true';
    }

    this.isRTL    = getComputedStyle(this).direction === 'rtl';
    this.duration = parseFloat(this.dataset.marqueeSpeed) || 30;
    this.offset   = 0;
    this.hovered  = false;
    this.dragging = false;
    this.moved    = false;

    this._measure();
    this.resizeObserver = new ResizeObserver(() => this._measure());
    this.resizeObserver.observe(this.track);

    this.addEventListener('mouseenter', this._onEnter);
    this.addEventListener('mouseleave', this._onLeave);
    this.track.addEventListener('pointerdown', this._onPointerDown);
    this.track.addEventListener('click', this._suppressClickAfterDrag, true);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());

    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this._tick);
  }

  _teardownMarquee() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    window.removeEventListener('pointermove',   this._onPointerMove);
    window.removeEventListener('pointerup',     this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  }

  _measure() {
    if (!this.track) return;
    this.half  = this.track.scrollWidth / 2;
    this.speed = this.half / this.duration; // px / second
  }

  _tick = (now) => {
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    const running = !this.hovered && !this.dragging && !this.pausedByModal && this.half > 0;
    if (running) this.offset += (this.isRTL ? 1 : -1) * this.speed * dt;

    this._normalize();
    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
    this.raf = requestAnimationFrame(this._tick);
  };

  _normalize() {
    const h = this.half;
    if (!h) return;
    if (this.isRTL) {
      this.offset = ((this.offset % h) + h) % h;
    } else {
      this.offset = -(((-this.offset % h) + h) % h);
    }
  }

  _onEnter = () => (this.hovered = true);
  _onLeave = () => (this.hovered = false);

  _onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.dragging    = true;
    this.moved       = false;
    this.startX      = e.clientX;
    this.startOffset = this.offset;
    this.track.classList.add('is-dragging');
    window.addEventListener('pointermove',   this._onPointerMove);
    window.addEventListener('pointerup',     this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  };

  _onPointerMove = (e) => {
    if (!this.dragging) return;
    const dx = e.clientX - this.startX;
    if (Math.abs(dx) > 5) this.moved = true;
    this.offset = this.startOffset + dx;
  };

  _onPointerUp = () => {
    if (!this.dragging) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    window.removeEventListener('pointermove',   this._onPointerMove);
    window.removeEventListener('pointerup',     this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  };

  _suppressClickAfterDrag = (e) => {
    if (this.moved) {
      e.stopPropagation();
      e.preventDefault();
      this.moved = false;
    }
  };
}

/* ============================================================
   ReviewVideoLightbox custom element
   ============================================================ */
class ReviewVideoLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog  = this.querySelector('dialog');
    this.video   = this.querySelector('[data-rv-vlb-video]');

    this.querySelector('[data-rv-vlb-close]')?.addEventListener('click', () => this.close());

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('close', () => {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.innerHTML = '';
      this.video.load();
      // Resume grid videos with their original muted state preserved
      document.querySelectorAll('reviews-videos').forEach((el) => el.resumeVisible?.());
    });
  }

  /**
   * Open the fullscreen player.
   * @param {HTMLVideoElement} sourceVideo - the thumbnail video from the grid
   * @param {{ muted?: boolean }} options
   *   muted: driven by the section's lightbox_muted setting (independent from grid mute)
   */
  open(sourceVideo, { muted = false } = {}) {
    if (!sourceVideo) return;

    this.video.innerHTML = sourceVideo.innerHTML;
    if (sourceVideo.poster) this.video.poster = sourceVideo.poster;
    this.video.load();


    if (!this.dialog.open) this.dialog.showModal();

    this.video.muted = muted;
    this.video.play().catch(() => {
      if (!this.video.muted) {
        // Browser blocked unmuted autoplay — fall back silently
        this.video.muted = true;
        this.video.play().catch(() => {});
      }
    });
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }
}

/* ---- Register custom elements ---- */
if (!customElements.get('reviews-videos'))       customElements.define('reviews-videos',       ReviewsVideos);
if (!customElements.get('review-video-lightbox')) customElements.define('review-video-lightbox', ReviewVideoLightbox);